"use client";

import { GripVertical, Menu, MoreVertical, SquarePen, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { useIsMobile } from "@/core/hooks/useIsMobile";
import { useActionsTrack } from "@/core/services/menu-analytics";
import { saveChatState } from "@/core/utils/chatbot-storage";
import { cn } from "@/core/lib/utils";
import { HeaderAgentIcon, WelcomeAgentIcon } from "../constants/agents";
import { useChatSession } from "../hooks/useChatSession";
import { BetaTag } from "./beta-tag";
import { ChatInput } from "./chat-input";
import { ChatSessionMessages } from "./chat-session-messages";
import { ConversationHistoryPanel } from "./conversation-history-panel";

type AgentSidePanelProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const TRIGGER_DEFAULT_RIGHT = 24;
const TRIGGER_DEFAULT_BOTTOM = 24;

function clampTriggerPosition(
  right: number,
  bottom: number,
  width: number,
  height: number,
) {
  const maxRight = Math.max(0, window.innerWidth - width);
  const maxBottom = Math.max(0, window.innerHeight - height);
  return {
    right: Math.max(0, Math.min(maxRight, right)),
    bottom: Math.max(0, Math.min(maxBottom, bottom)),
  };
}

/**
 * Estado fechado do painel: mesmo visual e comportamento do botão compacto do
 * chat flutuante legado (card branco + grip + ícone + pill com gradiente),
 * incluindo o arrasto pelo grip ou pela própria pill.
 */
function AgentPanelTrigger({ onOpen }: { onOpen: () => void }) {
  const [translate] = useTranslation(["core"]);
  const [position, setPosition] = useState({
    right: TRIGGER_DEFAULT_RIGHT,
    bottom: TRIGGER_DEFAULT_BOTTOM,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
  } | null>(null);
  const hasDraggedRef = useRef(false);

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current || !containerRef.current) return;
    hasDraggedRef.current = true;
    const { offsetWidth, offsetHeight } = containerRef.current;
    const deltaX = dragRef.current.startX - e.clientX;
    const deltaY = dragRef.current.startY - e.clientY;
    setPosition(
      clampTriggerPosition(
        dragRef.current.startRight + deltaX,
        dragRef.current.startBottom + deltaY,
        offsetWidth,
        offsetHeight,
      ),
    );
  }, []);

  const handleDragEnd = useCallback(() => {
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.body.style.userSelect = "";
    dragRef.current = null;
  }, [handleDragMove]);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      hasDraggedRef.current = false;
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startRight: position.right,
        startBottom: position.bottom,
      };
    },
    [position, handleDragMove, handleDragEnd],
  );

  // Remove listeners do documento se o componente desmontar durante um arrasto
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.body.style.userSelect = "";
    };
  }, [handleDragMove, handleDragEnd]);

  // Clique na pill só abre o painel se não houve arrasto no mesmo gesto.
  const handlePillClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasDraggedRef.current) {
        e.preventDefault();
        return;
      }
      onOpen();
    },
    [onOpen],
  );

  const title = [
    translate("chatbot-title-facilities-prefix"),
    translate("chatbot-title-facilities-emphasis"),
    translate("chatbot-title-facilities-suffix"),
  ].join(" ");

  return (
    <div
      ref={containerRef}
      className="fixed z-40 select-none"
      style={{ right: position.right, bottom: position.bottom }}
    >
      <div className="flex items-center gap-2 rounded-2xl bg-white px-2 py-3 shadow-lg">
        <button
          type="button"
          onMouseDown={(e) => {
            e.stopPropagation();
            handleDragStart(e);
          }}
          aria-label={translate("chatbot-aria-drag-window") as string}
          className="-ml-1 flex shrink-0 cursor-grab items-center justify-center text-darkgray-100 active:cursor-grabbing touch-none"
        >
          <GripVertical className="h-5 w-5" strokeWidth={2} />
        </button>

        <img
          src={WelcomeAgentIcon}
          alt=""
          aria-hidden
          className="h-11 w-11 shrink-0"
        />

        <button
          type="button"
          onMouseDown={handleDragStart}
          onClick={handlePillClick}
          className={cn(
            "flex h-11 items-center gap-2 rounded-full rounded-bl-none px-6",
            "bg-agent-facilities-pill text-[16px] font-bold text-white whitespace-nowrap",
            "transition-transform duration-200 hover:scale-[1.02] active:scale-100",
            "focus:outline-none focus:ring-2 focus:ring-aiBlue-400 focus:ring-offset-2",
          )}
          aria-label={translate("chatbot-panel-open") as string}
          data-testid="agent-panel-trigger"
        >
          {title}
          <BetaTag variant="onDark" />
        </button>
      </div>
    </div>
  );
}

/**
 * Painel lateral do agente (PED-12262, Fase 3): host docked do motor de chat
 * usado nas telas comuns do shell WOS. Substitui a bolha flutuante —
 * fica fixo à direita do conteúdo (sticky em desktop, overlay no mobile) e a
 * conversa continua ao navegar entre telas, pois o painel permanece montado
 * fora do Outlet.
 */
export function AgentSidePanel({ isOpen, onOpenChange }: AgentSidePanelProps) {
  const [translate] = useTranslation(["core"]);
  const isMobile = useIsMobile();
  const { onActionClick } = useActionsTrack();

  const session = useChatSession({ isActive: isOpen });
  const {
    inputValue,
    setInputValue,
    isLoading,
    hydrated,
    userInfo,
    conversations,
    activeConversationId,
    isHistoryOpen,
    setIsHistoryOpen,
    sendMessage,
    handleEnter,
    handleNewConversation,
    handleOpenHistory,
    handleCloseHistory,
    handleSelectConversation,
    handleRemoveConversation,
    setExtractionResult,
    setExtractionConfirmed,
  } = session;

  // Trava o scroll do body quando o painel vira overlay no mobile.
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, isOpen]);

  const handleOpen = () => {
    onActionClick("CHATBOT_CLICK_IA_AGENT", "CHATBOT");
    setIsHistoryOpen(false);
    onOpenChange(true);
  };

  const handleClose = () => {
    setIsHistoryOpen(false);
    onOpenChange(false);
    saveChatState(false);
  };

  if (!isOpen) {
    return <AgentPanelTrigger onOpen={handleOpen} />;
  }

  return (
    <aside
      className={cn(
        isMobile
          ? "fixed inset-0 z-50 p-0"
          : "h-full max-h-svh w-[420px] shrink-0 py-4 pr-4",
      )}
      data-testid="agent-side-panel"
    >
      <div
        className={cn(
          "flex h-full flex-col overflow-hidden bg-card",
          isMobile ? "rounded-none" : "rounded-[32px]",
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src={HeaderAgentIcon}
              alt=""
              aria-hidden
              className="size-8 object-contain"
            />
            <h3 className="truncate text-[15px] font-semibold text-foreground">
              <span>{translate("chatbot-title-facilities-prefix")}</span>{" "}
              <i className="font-serif text-[16px] italic">
                {translate("chatbot-title-facilities-emphasis")}
              </i>{" "}
              <span>{translate("chatbot-title-facilities-suffix")}</span>
            </h3>
            <BetaTag />
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground"
                  aria-label={translate("chatbot-aria-actions-menu") as string}
                >
                  <MoreVertical className="h-5 w-5" strokeWidth={1} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => handleNewConversation("header")}
                >
                  <SquarePen className="mr-2 h-4 w-4" />
                  {translate("chatbot-history-new-conversation")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={
                    isHistoryOpen ? handleCloseHistory : handleOpenHistory
                  }
                >
                  <Menu className="mr-2 h-4 w-4" />
                  {translate(
                    isHistoryOpen
                      ? "chatbot-history-close"
                      : "chatbot-history-open",
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0 text-muted-foreground"
              aria-label={translate("chatbot-aria-close-chat") as string}
              data-testid="agent-panel-close"
            >
              <X className="h-5 w-5" strokeWidth={1} />
            </Button>
          </div>
        </div>

        {/* Corpo */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {isHistoryOpen ? (
            <ConversationHistoryPanel
              conversations={conversations}
              activeConversationId={activeConversationId}
              onNewConversation={() => handleNewConversation("history_panel")}
              onSelectConversation={handleSelectConversation}
              onRemoveConversation={handleRemoveConversation}
            />
          ) : (
            <ChatSessionMessages session={session} />
          )}
        </div>

        {/* Input */}
        {!isHistoryOpen && (
          <div className="shrink-0">
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={sendMessage}
              onEnter={handleEnter}
              variant="action-center"
              onNewConversation={() => handleNewConversation("header")}
              disabled={isLoading || !hydrated}
              allowedDomains={userInfo?.league?.domains ?? []}
              onFileParsed={(result) => {
                setExtractionResult(result);
                setExtractionConfirmed(false);
              }}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
